import { Component, Input, ViewChild, EventEmitter, Output, OnInit } from '@angular/core';
import { ContextMenuComponent } from 'ngx-contextmenu';
import { ApplicationEventService, MatchService } from '../../../services';
import { MatchEvent, CodeEventGroup, Match } from '../../../models';
import { MediaLoadedEvent } from '../../../typings';
import { MatDialog } from '@angular/material';
import { EditCodeEventDialog } from '../../dialogs/edit-code-event-dialog';
import { AdjustRowTimingsDialog } from '../../dialogs';

@Component({
  selector: 'timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.css']
})
export class TimelineComponent implements OnInit {
  @ViewChild('eventMenu') public eventMenu: ContextMenuComponent;
  @ViewChild('rowMenu') public rowMenu: ContextMenuComponent;

  @Input() match: Match;
  @Input() isPublic: boolean;

  @Output() readonly deleteCodeEvent = new EventEmitter<MatchEvent>();
  @Output() readonly showPlaylist = new EventEmitter<CodeEventGroup>();
  @Output() readonly exportRowVideo = new EventEmitter<CodeEventGroup>();
  @Output() readonly addToPlaylist = new EventEmitter<MatchEvent[]>();
  @Output() readonly viewCodeClip = new EventEmitter<MatchEvent>();

  intervalMode = 'Minutes';
  videoDuration = 60 * 60; // 60 minutes in seconds
  segmentCount = 12;
  segments: number[]; // = [1,2,3,4,5,6,7,8,9,10,11,12];

  constructor(
    protected matchService: MatchService,
    protected applicationEventService: ApplicationEventService,
    public dialog: MatDialog
  ) {

  }

  ngOnInit(): void {
    this.segments = Array(this.segmentCount)
      .fill(1)
      .map((x, i) => i + 1);

    this.match.events = this.match.events.sort((g1: CodeEventGroup, g2: CodeEventGroup) => {
      return g1.eventType < g2.eventType ? -1 : g1.eventType === g2.eventType ? 0 : 1;
    });

    this.applicationEventService.mediaLoaded$.subscribe((e: MediaLoadedEvent) => {
      this.onMediaLoaded(e);
    });
  }

  playClips(group: CodeEventGroup) {
    (<any>window).gtag('event', 'playClips', {
      'event_category' : 'timeline',
      'event_label' : group.eventType
    });

    this.showPlaylist.emit(group);
  }

  onSelected(matchEvent: MatchEvent) {
    this.applicationEventService.navigateTo(matchEvent);
  }

  onMediaLoaded(e: MediaLoadedEvent) {
    console.info(`[timeline.mediaLoaded] - duration ${e.duration}`);
    this.videoDuration = e.duration;
  }

  // Item Context Menu Actions

  onEditEventClicked(matchEvent: MatchEvent) {
    console.info(`Edit clicked for CodeEvent: `, matchEvent);

    (<any>window).gtag('event', 'editEvent', {
      'event_category' : 'timeline',
      'event_label' : matchEvent.eventType
    });

    const dialogRef = this.dialog.open(EditCodeEventDialog, {
      height: '475px',
      width: '600px',
      data: matchEvent
    });

    dialogRef.afterClosed().subscribe((codeEvent: MatchEvent) => {
      if (codeEvent) {
        console.info('After CodeEvent update: ', codeEvent);
        this.matchService.update(this.match);
      }
    });
  }

  onDeleteItemClicked(matchEvent: MatchEvent) {
    console.info('[Timeline]:onDeleteItemClicked', matchEvent);

    (<any>window).gtag('event', 'deleteEvent', {
      'event_category' : 'timeline',
      'event_label' : matchEvent.eventType
    });

    this.deleteCodeEvent.emit(matchEvent);
  }

  onViewClipClicked(matchEvent: MatchEvent) {

    (<any>window).gtag('event', 'viewClip', {
      'event_category' : 'timeline',
      'event_label' : matchEvent.eventType
    });

    this.viewCodeClip.emit(matchEvent);
  }

  onAddToPlaylistClicked(matchEvent: MatchEvent) {

    (<any>window).gtag('event', 'addToPlaylist', {
      'event_category' : 'timeline',
      'event_label' : matchEvent.eventType
    });

    this.addToPlaylist.emit([matchEvent]);
  }

  // Row Context Menu Actions

  onAdjustRowTimings(group: CodeEventGroup) {
    console.info(`AdjustRowTimings clicked for CodeEventGroup: `, group);

    (<any>window).gtag('event', 'adjustRowTimings', {
      'event_category' : 'timeline',
      'event_label' : group.eventType
    });

    const events = group.events;

    const dialogRef = this.dialog.open(AdjustRowTimingsDialog, {
      height: '425px',
      width: '480px',
      data: events
    });

    dialogRef.afterClosed().subscribe((grp: CodeEventGroup) => {
      if (grp) {
        console.info('After AdjustRowTimings update: ', grp);
        this.matchService.update(this.match);

        const r = this.match.events.map(g => {
          if (g.eventType === grp.eventType) {
            return grp;
          }
          return g;
        });

        const match = { ...this.match, r };

        this.matchService.update(match);
      }
    });
  }

  onAddRowToPlaylistClicked(group: CodeEventGroup) {
    if (confirm(`Are you sure you want to add all ${group.eventType} items to a playlist?`)) {

      (<any>window).gtag('event', 'addRowToPlaylist', {
        'event_category' : 'timeline',
        'event_label' : group.eventType
      });

      const matchEvents = group.events;
      this.addToPlaylist.emit(matchEvents);
    }
  }

  onExportVideoClick(group: CodeEventGroup) {

    (<any>window).gtag('event', 'exportGroup', {
      'event_category' : 'timeline',
      'event_label' : group.eventType
    });

    this.exportRowVideo.emit(group);
  }

  onRowClipsClicked(group: CodeEventGroup) {

    (<any>window).gtag('event', 'showRowClips', {
      'event_category' : 'timeline',
      'event_label' : group.eventType
    });

    this.showPlaylist.emit(group);
  }

  calculateStyleOffsetFromIndex(index: number): string {
    const totalMinutes = this.videoDuration / 60;
    const minutesPerSegment = totalMinutes / this.segments.length;
    const currentMinutes = minutesPerSegment * index;
    const percentage = currentMinutes / totalMinutes * 100;
    return `${percentage}%`;
  }

  calculateStyleOffsetFromSeconds(seconds: number): string {
    // console.info(seconds)
    const percentage = seconds / this.videoDuration * 100;
    return `${percentage}%`;
  }

  calculateTimeInterval(index: number): number {
    let interval = -1;

    if (this.videoDuration > 300) {
      this.intervalMode = 'Minutes';
      const totalMinutes = this.videoDuration / 60;
      const minutesPerSegment = totalMinutes / this.segments.length;
      interval = minutesPerSegment * index;
    } else {
      const secondsPerSegment = this.videoDuration / this.segments.length;
      interval = secondsPerSegment * index;
      this.intervalMode = 'Seconds';
    }

    return Math.round(interval);
  }

  getLeftPixels(matchEvent: MatchEvent): number {
    const containerWidth = 800;
    const val = containerWidth * this.getLeftPercent(matchEvent.seconds);
    return val;
  }

  getLeftPercent(elapsed: number): number {
    const maximumSeconds = 60 * 70;
    return elapsed / maximumSeconds;
  }
}
